import { collect } from "../../graph/paging.ts";
import { MODULE_SCOPES } from "../../auth/config.ts";
import { InteractionRequiredError, NotSignedInError } from "../../auth/broker.ts";
import { GraphError } from "../../graph/client.ts";
import { writeFile } from "../../storage/fsa.ts";
import { putItem } from "../../storage/idb.ts";
import type { RunLogger } from "../../export/run.ts";
import { ExportAbortedError, throwIfAborted } from "../../export/abort.ts";
import { withinRange, isContainerSelected, type DateRange, type ContainerSelection } from "../../export/plan.ts";
import { listKey, listPath } from "./paths.ts";
import { renderTaskListJson, renderTask } from "./render.ts";
import { renderTasksIndex, type TaskListGroup } from "./index-render.ts";
import type { TodoTaskList, TodoTask } from "./types.ts";

/**
 * Tasks export (ARCHITECTURE.md §15 step 8).
 *
 * To Do has no `/delta` for tasks (§8 names it explicitly among the modules
 * that lack one), and a `todoTaskList` carries no list-level "last changed"
 * signal the way a Teams chat's `lastUpdatedDateTime` does — there is no
 * cheap way to ask "did this list change" short of listing it. So every run
 * re-lists and rewrites every list's file in full, rather than skipping
 * lists a previous run already finished. For a personal To Do (tens of
 * tasks, not thousands) that cost is trivial, and it is the only way to
 * guarantee a task added after a previous export is not silently missed
 * forever — unlike every other module here, this one makes no
 * "already-done items are skipped" resume promise. `putItem` below still
 * records each list's outcome, but only so `moduleProgress`'s byte total
 * includes Tasks — never to decide whether to skip a list.
 */

const SCOPES = MODULE_SCOPES.tasks;

export interface TasksProgress {
    listsTotal: number;
    listsDone: number;
    currentLabel?: string;
    totalBytes: number;
}

export type TasksProgressCallback = (progress: TasksProgress) => void;

export interface TasksListSummary {
    label: string;
    tasks: number;
    bytes: number;
}

export interface ExportTasksParams {
    runId: string;
    root: FileSystemDirectoryHandle;
    /** From the Scan step: only export these lists. */
    selection?: ContainerSelection;
    /** From the Scan step: keep only tasks last modified within this window. */
    dateRange?: DateRange;
    onProgress?: TasksProgressCallback;
    onListComplete?: (summary: TasksListSummary) => void;
    /** Aborts at the next clean checkpoint. */
    signal?: AbortSignal;
    logger: RunLogger;
}

export interface TasksResult {
    listsTotal: number;
    tasks: number;
    bytes: number;
    errors: number;
}

export async function exportTasks(params: ExportTasksParams): Promise<TasksResult> {
    const { runId, root, selection, dateRange, onProgress, onListComplete, signal, logger } = params;

    await logger.info("listing task lists");
    const allLists = await collect<TodoTaskList>("/me/todo/lists", {
        scopes: SCOPES,
        service: "outlook",
        ...(signal ? { signal } : {})
    });
    const lists = allLists.filter((l) => isContainerSelected(selection, l.id));
    await logger.info(`found ${allLists.length} lists, ${lists.length} selected`);

    let totalBytes = 0;
    let totalTasks = 0;
    let errors = 0;
    const groups: TaskListGroup[] = [];

    for (let i = 0; i < lists.length; i += 1) {
        throwIfAborted(signal);

        const list = lists[i];
        if (!list) {
            continue;
        }

        const label = list.displayName || "list";
        onProgress?.({ listsTotal: lists.length, listsDone: i, currentLabel: label, totalBytes });

        try {
            const allTasks = await collect<TodoTask>(`/me/todo/lists/${list.id}/tasks`, {
                scopes: SCOPES,
                service: "outlook",
                ...(signal ? { signal } : {})
            });
            // Date filter (Scan step) on each task's last-modified (fall back to
            // created) date.
            const tasks = dateRange
                ? allTasks.filter((t) => withinRange(t.lastModifiedDateTime ?? t.createdDateTime, dateRange))
                : allTasks;

            const key = await listKey(list);
            const written = await writeFile(root, listPath(key), renderTaskListJson(tasks));
            await putItem({ runId, module: "tasks", itemId: list.id, status: "done", bytes: written });

            totalTasks += tasks.length;
            totalBytes += written;
            groups.push({ key, label, tasks: tasks.map(renderTask) });
            onListComplete?.({ label, tasks: tasks.length, bytes: written });
        } catch (err) {
            if (
                err instanceof ExportAbortedError ||
                err instanceof InteractionRequiredError ||
                err instanceof NotSignedInError
            ) {
                throw err;
            }
            // This list's problem alone — logged and left to retry on the
            // next run (which, per the note above, always retries every list
            // regardless), everything else keeps going.
            errors += 1;
            const message = err instanceof GraphError ? err.toLogString() : String(err);
            await logger.error(`list failed, will retry on resume: ${message}`);
            onListComplete?.({ label, tasks: 0, bytes: 0 });
        }
    }

    // Idempotent and network-free, like every other module's index pass —
    // rebuilt each run from the lists just fetched above.
    await renderTasksIndex(root, groups);

    onProgress?.({ listsTotal: lists.length, listsDone: lists.length, totalBytes });
    await logger.info(
        `tasks export complete: ${totalTasks} tasks across ${lists.length} lists, ${errors} errors`
    );

    return { listsTotal: lists.length, tasks: totalTasks, bytes: totalBytes, errors };
}
